import isEqual from "lodash/isEqual";
import cloneDeep from "lodash/cloneDeep";

import { putRequestCase, postRequestCase, deleteRequestCase } from "@/stores/caseStore/backendApi";
import { useCaseStore } from "@/stores/caseStore/0_caseStore";
import { withQuestionnaireSource } from "@/stores/caseStore/questionnaireSource";
import { useSnackbarStore } from "@/stores/snackbarStore";
import * as d from "@/utils/dataUtils";

// model

export const defaultOpponent = {
  category: null,
  subcategory: null,
  subcategoryOther: "",
  otherAgeCategory: null,

  registration: null,
  registrationKnown: null,
  licensePlate: "",
  licenseCountry: null,
  // Fahrzeugdaten aus dem Fahrzeugschein (Kundenfeedback 30.09.2026, IV)
  vehicleManufacturer: "",
  vehicleModel: "",
  vehicleFirstRegistration: null,

  holderDataAvailable: null,
  holderType: null,
  holderCompany: "",
  holderSalutation: null,
  holderTitle: null,
  holderFirstName: "",
  holderLastName: "",
  holderStreetNo: "",
  holderZip: "",
  holderCity: "",
  holderCountry: null,
  holderPhone: "",
  holderEmail: "",
  holderHomepage: "",
  holderRepresentedByLawyer: null,
  holderLawyerCaseNo: "",
  holderLawfirm: "",
  holderLawyer: "",

  driverDataAvailable: null,
  driverSalutation: null,
  driverTitle: null,
  driverFirstName: "",
  driverLastName: "",
  driverStreetNo: "",
  driverZip: "",
  driverCity: "",
  driverCountry: null,
  driverPhone: "",
  driverEmail: "",
  driverHomepage: "",
  driverRepresentedByLawyer: null,
  driverLawyerCaseNo: "",
  driverLawfirm: "",
  driverLawyer: "",

  otherType: null,
  otherCompany: "",
  otherSalutation: null,
  otherTitle: null,
  otherFirstName: "",
  otherLastName: "",
  otherStreetNo: "",
  otherZip: "",
  otherCity: "",
  otherCountry: null,
  otherPhone: "",
  otherEmail: "",
  otherHomepage: "",
  otherRepresentedByLawyer: null,
  otherLawyerCaseNo: "",
  otherLawfirm: "",
  otherLawyer: "",

  mother: null,
  motherSalutation: null,
  motherTitle: null,
  motherFirstName: "",
  motherLastName: "",
  motherStreetNo: "",
  motherZip: "",
  motherCity: "",
  motherCountry: null,
  motherPhone: "",
  motherEmail: "",
  motherRepresentedByLawyer: null,
  motherLawyerCaseNo: "",
  motherLawfirm: "",
  motherLawyer: "",

  father: null,
  fatherSalutation: null,
  fatherTitle: null,
  fatherFirstName: "",
  fatherLastName: "",
  fatherStreetNo: "",
  fatherZip: "",
  fatherCity: "",
  fatherCountry: null,
  fatherPhone: "",
  fatherEmail: "",
  fatherRepresentedByLawyer: null,
  fatherLawyerCaseNo: "",
  fatherLawfirm: "",
  fatherLawyer: "",

  insuranceDataAvailable: null,
  insuranceType: null,
  insuranceName: "",
  insuranceCountry: null,
  insurancePolicyNumber: "",
  insuranceCaseNumber: "",
  insurancePhone: "",
  insuranceEmail: "",
  insuranceContact: "",
  insuranceRepresentedByLawyer: null,
  insuranceLawyerCaseNo: "",
  insuranceLawfirm: "",
  insuranceLawyer: "",

  adjusterDataAvailable: null,
  adjusterName: "",
  adjusterPolicyNumber: "",
  adjusterCaseNumber: "",
  adjusterPhone: "",
  adjusterEmail: "",
  adjusterContact: "",
  adjusterRepresentedByLawyer: null,
  adjusterLawyerCaseNo: "",
  adjusterLawfirm: "",
  adjusterLawyer: "",

  remarks: "",
};

// CRUD

/**
 * Fehler werden per Snackbar gemeldet und – wie bisher – NICHT weitergereicht.
 * Mit `{ rethrow: true }` zusätzlich weiterreichen (Fragebogen-Hülle: zeigt
 * dann „Nicht gespeichert“ statt „Gespeichert“).
 */
export async function saveOpponents(options: { rethrow?: boolean } = {}): Promise<void> {
  const store = useCaseStore();
  const snackbar = useSnackbarStore();

  try {
    const current = store.opponents.map(prepareOpponentForSave);
    const previous = store.lastSavedOpponents.map(prepareOpponentForSave);

    if (isEqual(current, previous)) return;

    for (const opponent of current) {
      await putRequestCase(withQuestionnaireSource(`/protected/cases/${store.caseData.id}/opponents/${opponent.id}`, store.questionnaireView), opponent);
    }

    store.lastSavedOpponents = cloneDeep(store.opponents);
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : (error as any)?.message || "Fehler beim Speichern";
    snackbar.showSnackbar({ message: msg, type: "error" });
    if (options.rethrow) throw error;
  }
}

export async function addOpponent(): Promise<void> {
  const store = useCaseStore();
  const response = await postRequestCase(`/protected/cases/${store.caseData.id}/opponents`);
  store.opponents.push(prepareOpponentForLoad(response));
  store.lastSavedOpponents = cloneDeep(store.opponents);
}

export async function deleteOpponent(idOpponent: string): Promise<void> {
  const store = useCaseStore();
  const index = store.opponents.findIndex((v) => String(v.id) === String(idOpponent));
  if (index === -1) return;

  // Kundenfeedback 26.10.2026 (VIII): Löschen aus dem Fragebogen – ein
  // Eintrag mit Inhalt bleibt in der Akte, bis die Kanzlei entscheidet.
  await deleteRequestCase(withQuestionnaireSource(`/protected/cases/${store.caseData.id}/opponents/${idOpponent}`, store.questionnaireView));
  store.opponents.splice(index, 1);
  store.lastSavedOpponents = cloneDeep(store.opponents);
}

// data helper

type Opponent = Record<string, any>;

export function prepareOpponentForLoad(data: Opponent): Opponent {
  return {
    ...data,

    category: d.loadSelect(data.category),
    subcategory: d.loadSelect(data.subcategory),
    otherAgeCategory: d.loadSelect(data.otherAgeCategory),

    registration: d.loadTristate(data.registration),
    registrationKnown: d.loadTristate(data.registrationKnown),
    licensePlate: data.licensePlate,
    licenseCountry: d.loadSelect(data.licenseCountry),
    vehicleFirstRegistration: d.loadDate(data.vehicleFirstRegistration),

    holderDataAvailable: d.loadTristate(data.holderDataAvailable),
    holderType: d.loadSelect(data.holderType),
    holderSalutation: d.loadSelect(data.holderSalutation),
    holderTitle: d.loadSelect(data.holderTitle),
    holderCountry: d.loadSelect(data.holderCountry),
    holderRepresentedByLawyer: d.loadTristate(data.holderRepresentedByLawyer),

    driverDataAvailable: d.loadTristate(data.driverDataAvailable),
    driverSalutation: d.loadSelect(data.driverSalutation),
    driverTitle: d.loadSelect(data.driverTitle),
    driverCountry: d.loadSelect(data.driverCountry),
    driverRepresentedByLawyer: d.loadTristate(data.driverRepresentedByLawyer),

    otherType: d.loadSelect(data.otherType),
    otherSalutation: d.loadSelect(data.otherSalutation),
    otherTitle: d.loadSelect(data.otherTitle),
    otherCountry: d.loadSelect(data.otherCountry),
    otherRepresentedByLawyer: d.loadTristate(data.otherRepresentedByLawyer),

    mother: d.loadTristate(data.mother),
    motherSalutation: d.loadSelect(data.motherSalutation),
    motherTitle: d.loadSelect(data.motherTitle),
    motherCountry: d.loadSelect(data.motherCountry),
    motherRepresentedByLawyer: d.loadTristate(data.motherRepresentedByLawyer),

    father: d.loadTristate(data.father),
    fatherSalutation: d.loadSelect(data.fatherSalutation),
    fatherTitle: d.loadSelect(data.fatherTitle),
    fatherCountry: d.loadSelect(data.fatherCountry),
    fatherRepresentedByLawyer: d.loadTristate(data.fatherRepresentedByLawyer),

    insuranceDataAvailable: d.loadTristate(data.insuranceDataAvailable),
    insuranceType: d.loadSelect(data.insuranceType),
    insuranceCountry: d.loadSelect(data.insuranceCountry),
    insuranceRepresentedByLawyer: d.loadTristate(data.insuranceRepresentedByLawyer),

    adjusterDataAvailable: d.loadTristate(data.adjusterDataAvailable),
    adjusterRepresentedByLawyer: d.loadTristate(data.adjusterRepresentedByLawyer),
  };
}

export function prepareOpponentForSave(data: Opponent): Opponent {
  return {
    ...data,

    category: d.saveSelect(data.category),
    subcategory: d.saveSelect(data.subcategory),
    otherAgeCategory: d.saveSelect(data.otherAgeCategory),

    registration: d.saveTristate(data.registration),
    registrationKnown: d.saveTristate(data.registrationKnown),
    licensePlate: data.licensePlate,
    licenseCountry: d.saveSelect(data.licenseCountry),
    vehicleFirstRegistration: d.saveDate(data.vehicleFirstRegistration),

    holderDataAvailable: d.saveTristate(data.holderDataAvailable),
    holderType: d.saveSelect(data.holderType),
    holderSalutation: d.saveSelect(data.holderSalutation),
    holderTitle: d.saveSelect(data.holderTitle),
    holderCountry: d.saveSelect(data.holderCountry),
    holderRepresentedByLawyer: d.saveTristate(data.holderRepresentedByLawyer),

    driverDataAvailable: d.saveTristate(data.driverDataAvailable),
    driverSalutation: d.saveSelect(data.driverSalutation),
    driverTitle: d.saveSelect(data.driverTitle),
    driverCountry: d.saveSelect(data.driverCountry),
    driverRepresentedByLawyer: d.saveTristate(data.driverRepresentedByLawyer),

    otherType: d.saveSelect(data.otherType),
    otherSalutation: d.saveSelect(data.otherSalutation),
    otherTitle: d.saveSelect(data.otherTitle),
    otherCountry: d.saveSelect(data.otherCountry),
    otherRepresentedByLawyer: d.saveTristate(data.otherRepresentedByLawyer),

    mother: d.saveTristate(data.mother),
    motherSalutation: d.saveSelect(data.motherSalutation),
    motherTitle: d.saveSelect(data.motherTitle),
    motherCountry: d.saveSelect(data.motherCountry),
    motherRepresentedByLawyer: d.saveTristate(data.motherRepresentedByLawyer),

    father: d.saveTristate(data.father),
    fatherSalutation: d.saveSelect(data.fatherSalutation),
    fatherTitle: d.saveSelect(data.fatherTitle),
    fatherCountry: d.saveSelect(data.fatherCountry),
    fatherRepresentedByLawyer: d.saveTristate(data.fatherRepresentedByLawyer),

    insuranceDataAvailable: d.saveTristate(data.insuranceDataAvailable),
    insuranceType: d.saveSelect(data.insuranceType),
    insuranceCountry: d.saveSelect(data.insuranceCountry),
    insuranceRepresentedByLawyer: d.saveTristate(data.insuranceRepresentedByLawyer),

    adjusterDataAvailable: d.saveTristate(data.adjusterDataAvailable),
    adjusterRepresentedByLawyer: d.saveTristate(data.adjusterRepresentedByLawyer),
  };
}
